import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Stack,
  Typography,
} from '@mui/material';
import { Delete, Home, NavigateNext, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const CHECK_LABELS = {
  foto2: 'Foto 2',
  foto3: 'Foto 3',
  foto4: 'Foto 4',
  foto5: 'Foto 5',
  foto6: 'Foto 6',
};

const VALUE_LABELS = {
  ok: 'OK',
  parado: 'PARADO',
  nao_existe: 'NÃO EXISTE',
  operando: 'OPERANDO',
  inoperante: 'INOPERANTE',
  sem_rasgo: 'SEM RASGO',
  com_rasgo: 'COM RASGO',
  sem_amassados: 'SEM AMASSADOS',
  com_amassados: 'COM AMASSADOS',
  existente: 'EXISTENTE',
  nao_existente: 'NÃO EXISTENTE',
  com_carga: 'COM CARGA',
  sem_carga: 'SEM CARGA',
  nao_partiu: 'NÃO PARTIU',
  ruido_anormal: 'RUÍDO ANORMAL',
  medio: 'MÉDIO',
  baixo: 'BAIXO',
};

const PORTA_LABELS = {
  status: 'PORTA CÂMARA',
  lona: 'LONA',
  batente: 'BATENTE',
  sensor_fotocelula: 'SENSOR FOTOCÉLULA',
  sensor_difuso: 'SENSOR DIFUSO',
  pirulitos_protecao: 'PIRULITOS DE PROTEÇÃO',
  botoeira_interna: 'BOTOEIRA INTERNA',
  botoeira_externa: 'BOTOEIRA EXTERNA',
};

const STATUS_TRIPLA_COLS = ['OK', 'PARADO', 'NÃO EXISTE'];

const REFRIGERACAO_ITEMS = [
  'CASA DE MÁQUINAS',
  'BALCÃO AÇOUGUE',
  'BALCÃO PADARIA',
  'BALCÃO EMPÓRIO',
  'BALCÃO LATICÍNIOS',
  'BALCÃO FLV',
  'BALCÃO AUTO-SERVIÇO AÇOUGUE',
  'BALCÃO CHOPP',
  'ILHAS SELFIE',
  'CÂMARA CONGELADA',
  'CÂMARA RESFRIADA',
  'ANTE CÂMARA',
  'CÂMARA BOVINOS',
  'CÂMARA SUÍNOS',
  'CÂMARA FRANGOS',
  'CÂMARA SEBO E OSSO',
];

const AR_CONDICIONADO_ITEMS = [
  'SPLITÃO 1',
  'SPLITÃO 2',
  'SPLITÃO 3',
  'SPLIT GERÊNCIA',
  'SPLIT TESOURARIA',
  'SPLIT SALA DE DADOS',
  'SPLIT MONITORAMENTO',
  'SPLIT SALA DE CARTAZ',
  'SPLIT CPD',
  'SPLIT SALA DE DESCANSO 1',
  'SPLIT SALA DE DESCANSO 2',
  'SPLIT/PISO TETO REFEITÓRIO 1',
  'SPLIT/PISO TETO REFEITÓRIO 2',
  'SPLIT/PISO TETO REFEITÓRIO 3',
  'SPLIT SALA COFRE',
  'SPLIT SALA IFOOD',
];

const GERADOR_ITEMS = [
  'TIPO DE TESTE',
  'PARTIDA',
  'RUÍDO',
  'NÍVEL COMBUSTÍVEL NO TANQUE',
  'NÍVEL COMBUSTÍVEL NO RESERVATÓRIO',
  'NÍVEL DE ÁGUA',
  'NÍVEL DE ÓLEO DO MOTOR',
];

const PORTAS_ITEMS = [
  'PORTA CÂMARA',
  'LONA',
  'BATENTE',
  'SENSOR FOTOCÉLULA',
  'SENSOR DIFUSO',
  'PIRULITOS DE PROTEÇÃO',
  'BOTOEIRA INTERNA',
  'BOTOEIRA EXTERNA',
];

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
};

const sanitizeItemKey = (value = '') => value.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');

const normalizeLabel = (value = '') => {
  const lower = String(value).toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const buildOrderedLabelMap = (prefix, items) => {
  const map = {};
  items.forEach((item, index) => {
    map[`${prefix}_${sanitizeItemKey(item)}`] = `${index + 1}. ${normalizeLabel(item)}`;
  });
  return map;
};

const ORDERED_ITEM_LABELS = {
  ...buildOrderedLabelMap('refrigeracao', REFRIGERACAO_ITEMS),
  ...buildOrderedLabelMap('ar_condicionado', AR_CONDICIONADO_ITEMS),
  ...buildOrderedLabelMap('gerador', GERADOR_ITEMS),
  ...buildOrderedLabelMap('portas_resfriada', PORTAS_ITEMS),
  ...buildOrderedLabelMap('portas_congelada', PORTAS_ITEMS),
  ...buildOrderedLabelMap('resfriada', PORTAS_ITEMS),
  ...buildOrderedLabelMap('congelada', PORTAS_ITEMS),
};

const GERADOR_FIELD_LABELS = {
  tipo_teste: '1. Tipo de teste',
  partida: '2. Partida',
  ruido: '3. Ruído',
  nivel_comb_tanque: '4. Nível combustível no tanque',
  nivel_comb_reservatorio: '5. Nível combustível no reservatório',
  nivel_agua: '6. Nível de água',
  nivel_oleo_motor: '7. Nível de óleo do motor',
};

const PORTA_FIELD_LABELS = {
  status: '1. Porta câmara',
  lona: '2. Lona',
  batente: '3. Batente',
  sensor_fotocelula: '4. Sensor fotocélula',
  sensor_difuso: '5. Sensor difuso',
  pirulitos_protecao: '6. Pirulitos de proteção',
  botoeira_interna: '7. Botoeira interna',
  botoeira_externa: '8. Botoeira externa',
};

const formatValueLabel = (value) => {
  if (!value) return '-';
  return VALUE_LABELS[value] || String(value).replace(/_/g, ' ').toUpperCase();
};

const countFilledValues = (value) => {
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + countFilledValues(item), 0);
  }
  if (!value || typeof value !== 'object') {
    return value ? 1 : 0;
  }
  return Object.values(value).reduce((sum, item) => sum + countFilledValues(item), 0);
};

const getChecklistResumo = (item) => {
  if (item?.checklist_detalhado) {
    const total = countFilledValues(item.checklist_detalhado);
    return `Checklist completo: ${total} respostas preenchidas`;
  }
  if (item?._isLightweight && item?.has_detalhado) {
    return `Checklist completo: ${item.resumo_count || 0} respostas preenchidas`;
  }
  const checks = item?.checks || {};
  const marcados = Object.values(checks).filter(Boolean).length;
  const totalChecks = Object.keys(checks).length;
  return `Checklist simples: ${marcados}/${totalChecks} marcações`;
};

const renderSequencialDetalhe = (dataMap = {}) => (
  <Box sx={{ overflow: 'auto', maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1 }}>
    <Box
      sx={{
        minWidth: 680,
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 2fr) repeat(3, minmax(120px, 1fr))',
        columnGap: 1,
        rowGap: 0.35,
        alignItems: 'center',
      }}
    >
      <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper' }} />
      {STATUS_TRIPLA_COLS.map((col) => (
        <Typography
          key={`col-${col}`}
          sx={{
            fontWeight: 700,
            fontSize: '0.82rem',
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper',
            py: 0.5,
          }}
        >
          {col}
        </Typography>
      ))}

      {Object.entries(dataMap).map(([key, value]) => (
        <React.Fragment key={`seq-${key}`}>
          <Typography sx={{ fontSize: '0.88rem' }}>{key}:</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{value === 'ok' ? 'OK (X)' : 'OK ( )'}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>{value === 'parado' ? 'PARADO (X)' : 'PARADO ( )'}</Typography>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {value === 'nao_existe' ? 'NÃO EXISTE (X)' : 'NÃO EXISTE ( )'}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  </Box>
);

export default function TestesChecklistConsulta() {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();

  const hoje = new Date();
  const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
  const hojeStr = hoje.toISOString().slice(0, 10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [filterLoja, setFilterLoja] = useState('');
  const [filterCriador, setFilterCriador] = useState('');
  const [dataInicio, setDataInicio] = useState(primeiroDiaMes);
  const [dataFim, setDataFim] = useState(hojeStr);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fotosTab, setFotosTab] = useState(0);
  const [viewingFoto, setViewingFoto] = useState(null);
  const [viewingFotoSrc, setViewingFotoSrc] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Carrega imagem para visualização ampliada
  useEffect(() => {
    if (!viewingFoto?.upload_id) {
      setViewingFotoSrc('');
      return;
    }

    let objectUrl = '';
    let active = true;

    fetch(`/api/uploads/${viewingFoto.upload_id}/download`, { headers })
      .then((response) => {
        if (!response.ok) throw new Error('Falha ao carregar imagem');
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setViewingFotoSrc(objectUrl);
      })
      .catch((err) => {
        console.error('Erro ao carregar imagem:', err);
        if (active) setViewingFotoSrc('');
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [viewingFoto?.upload_id, headers]);

  const UploadThumb = ({ foto, subtitle }) => {
    const [src, setSrc] = useState('');
    const [failed, setFailed] = useState(false);

    useEffect(() => {
      if (!foto?.upload_id) return undefined;

      let objectUrl = '';
      let active = true;

      fetch(`/api/uploads/${foto.upload_id}/download`, { headers })
        .then((response) => {
          if (!response.ok) throw new Error('Falha ao carregar imagem');
          return response.blob();
        })
        .then((blob) => {
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          setSrc(objectUrl);
        })
        .catch(() => {
          if (active) setFailed(true);
        });

      return () => {
        active = false;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }, [foto?.upload_id]);

    return (
      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, width: 150 }}>
        <Box
          component={src && !failed ? 'img' : 'div'}
          src={src || undefined}
          alt={foto?.nome || 'Foto'}
          sx={{
            width: '100%',
            height: 110,
            borderRadius: 1.5,
            objectFit: 'cover',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: '0.75rem',
          }}
        >
          {!src || failed ? 'Sem prévia' : null}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.8 }} noWrap title={foto?.nome || ''}>
          {foto?.nome || 'Foto'}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap title={subtitle}>
            {subtitle}
          </Typography>
        ) : null}
        {foto?.upload_id ? (
          <Button
            size="small"
            endIcon={<OpenInNew fontSize="small" />}
            sx={{ mt: 0.5, px: 0 }}
            onClick={() => setViewingFoto(foto)}
          >
            Abrir
          </Button>
        ) : null}
      </Paper>
    );
  };

  const buildItemFromMeta = (fileMeta, byName) => {
    const m = fileMeta.metadados || {};
    return {
      _isLightweight: true,
      arquivo_json_id: fileMeta.id,
      arquivo_json_nome: fileMeta.nome,
      checklist_id: m.checklist_id || fileMeta.id,
      loja: m.loja || '',
      criado_por: { nome: m.criador_nome || '' },
      criado_em: m.criado_em || fileMeta.created_at,
      data_checklist: m.data_checklist || '',
      checks: m.checks || {},
      observacoes: m.observacoes || '',
      has_detalhado: !!m.has_detalhado,
      resumo_count: m.resumo_count || 0,
      item_fotos: {},
      fotos: [],
      _byName: byName,
    };
  };

  const carregar = async (inicio = dataInicio, fim = dataFim) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({ entidade: 'testes_checklist' });
      if (inicio) params.set('data_inicio', inicio);
      if (fim) params.set('data_fim', fim);
      const listRes = await fetch(`/api/uploads?${params}`, { headers });
      if (!listRes.ok) throw new Error('Falha ao listar checklists.');

      const uploads = await listRes.json();
      const uploadItems = Array.isArray(uploads) ? uploads : [];
      const byName = new Map(uploadItems.map((item) => [item.nome, item]));
      const jsonUploads = uploadItems.filter((item) => item.nome?.startsWith('checklist_') && item.nome?.endsWith('.json'));

      // Split: items with metadados (fast) vs old items without (need download)
      const withMeta = jsonUploads.filter((f) => f.metadados);
      const withoutMeta = jsonUploads.filter((f) => !f.metadados);

      const lightweightItems = withMeta.map((f) => buildItemFromMeta(f, byName));

      const downloadedItems = (
        await Promise.all(
          withoutMeta.map(async (fileMeta) => {
            try {
              const fileRes = await fetch(`/api/uploads/${fileMeta.id}/download`, { headers });
              if (!fileRes.ok) return null;
              const content = await fileRes.text();
              const parsed = JSON.parse(content);
              const fotos = Array.isArray(parsed.fotos) ? parsed.fotos : [];
              const itemFotosRaw = parsed.item_fotos && typeof parsed.item_fotos === 'object' ? parsed.item_fotos : {};
              const itemFotos = Object.fromEntries(
                Object.entries(itemFotosRaw).map(([itemKey, fotosItem]) => [
                  itemKey,
                  (Array.isArray(fotosItem) ? fotosItem : []).map((f) => {
                    const nome = f?.nome || '';
                    const upload = byName.get(nome);
                    return { ...f, nome, upload_id: f?.upload_id || upload?.id || null };
                  }),
                ])
              );
              return {
                ...parsed,
                arquivo_json_id: fileMeta.id,
                arquivo_json_nome: fileMeta.nome,
                item_fotos: itemFotos,
                fotos: fotos.map((f) => {
                  const nome = f?.nome || '';
                  const upload = byName.get(nome);
                  return { ...f, nome, upload_id: f?.upload_id || upload?.id || null };
                }),
                _byName: byName,
              };
            } catch {
              return null;
            }
          })
        )
      ).filter(Boolean);

      const all = [...lightweightItems, ...downloadedItems];
      all.sort((a, b) => {
        const aTime = new Date(a?.criado_em || a?.data_checklist || 0).getTime();
        const bTime = new Date(b?.criado_em || b?.data_checklist || 0).getTime();
        return bTime - aTime;
      });

      setItems(all);
    } catch (err) {
      setError(err?.message || 'Erro ao carregar checklists.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhes = async (item) => {
    if (!item._isLightweight) {
      setSelected(item);
      return;
    }
    setDetailLoading(true);
    try {
      const byName = item._byName || new Map();
      const fileRes = await fetch(`/api/uploads/${item.arquivo_json_id}/download`, { headers });
      if (!fileRes.ok) throw new Error('Falha ao carregar checklist.');
      const content = await fileRes.text();
      const parsed = JSON.parse(content);
      const fotos = Array.isArray(parsed.fotos) ? parsed.fotos : [];
      const itemFotosRaw = parsed.item_fotos && typeof parsed.item_fotos === 'object' ? parsed.item_fotos : {};
      const itemFotos = Object.fromEntries(
        Object.entries(itemFotosRaw).map(([itemKey, fotosItem]) => [
          itemKey,
          (Array.isArray(fotosItem) ? fotosItem : []).map((f) => {
            const nome = f?.nome || '';
            const upload = byName.get(nome);
            return { ...f, nome, upload_id: f?.upload_id || upload?.id || null };
          }),
        ])
      );
      setSelected({
        ...parsed,
        arquivo_json_id: item.arquivo_json_id,
        arquivo_json_nome: item.arquivo_json_nome,
        item_fotos: itemFotos,
        fotos: fotos.map((f) => {
          const nome = f?.nome || '';
          const upload = byName.get(nome);
          return { ...f, nome, upload_id: f?.upload_id || upload?.id || null };
        }),
      });
    } catch (err) {
      setError(err?.message || 'Erro ao carregar detalhes do checklist.');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (token) carregar(dataInicio, dataFim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setFotosTab(0);
  }, [selected]);

  const formatItemLabel = (itemKey = '') => {
    const cleaned = itemKey
      .replace(/^refrigeracao_/, '')
      .replace(/^ar_condicionado_/, '')
      .replace(/^gerador_/, '')
      .replace(/^portas_resfriada_/, 'resfriada_')
      .replace(/^portas_congelada_/, 'congelada_')
      .replace(/_/g, ' ')
      .trim();
    return cleaned || itemKey;
  };

  const getItemLabelWithOrder = (itemKey = '') => {
    if (ORDERED_ITEM_LABELS[itemKey]) return ORDERED_ITEM_LABELS[itemKey];

    const normalized = String(itemKey);

    if (normalized.startsWith('gerador_')) {
      const field = normalized.replace('gerador_', '');
      if (GERADOR_FIELD_LABELS[field]) return GERADOR_FIELD_LABELS[field];
    }

    const portaPrefixes = ['portas_resfriada_', 'portas_congelada_', 'resfriada_', 'congelada_'];
    for (const prefix of portaPrefixes) {
      if (normalized.startsWith(prefix)) {
        const field = normalized.replace(prefix, '');
        if (PORTA_FIELD_LABELS[field]) {
          const tipo = prefix.includes('resfriada') ? 'Resfriada' : 'Congelada';
          return `${PORTA_FIELD_LABELS[field]} (${tipo})`;
        }
      }
    }

    return normalizeLabel(formatItemLabel(itemKey));
  };

  const groupedFotos = useMemo(() => {
    const empty = {
      refrigeracao: [],
      ar: [],
      gerador: [],
      portas: [],
      finalizacao: [],
    };
    if (!selected) return empty;

    Object.entries(selected.item_fotos || {}).forEach(([itemKey, fotosItem]) => {
      const normalized = (itemKey || '').toLowerCase();
      let target = 'gerador';
      if (normalized.startsWith('refrigeracao_')) target = 'refrigeracao';
      else if (normalized.startsWith('ar_condicionado_')) target = 'ar';
      else if (normalized.startsWith('portas_resfriada_') || normalized.startsWith('portas_congelada_')) target = 'portas';

      (fotosItem || []).forEach((foto, index) => {
        const prettyLabel = getItemLabelWithOrder(itemKey);
        empty[target].push({
          ...foto,
          _idx: index,
          _itemKey: itemKey,
          _itemLabel: prettyLabel,
        });
      });
    });

    empty.finalizacao = Array.isArray(selected.fotos) ? selected.fotos : [];
    return empty;
  }, [selected]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/uploads/${deleteTarget.arquivo_json_id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error('Falha ao excluir checklist.');
      setItems((prev) => prev.filter((i) => i.arquivo_json_id !== deleteTarget.arquivo_json_id));
      setDeleteTarget(null);
      if (selected?.arquivo_json_id === deleteTarget.arquivo_json_id) setSelected(null);
    } catch (err) {
      setError(err?.message || 'Erro ao excluir checklist.');
    } finally {
      setDeleting(false);
    }
  };

  const lojas = useMemo(() => {
    const set = new Set(items.map((i) => i?.loja || '').filter(Boolean));
    return [...set].sort();
  }, [items]);

  const criadores = useMemo(() => {
    const set = new Set(items.map((i) => i?.criado_por?.nome || '').filter(Boolean));
    return [...set].sort();
  }, [items]);

  const filtrados = useMemo(() => {
    return items.filter((item) => {
      if (filterLoja && (item?.loja || '') !== filterLoja) return false;
      if (filterCriador && (item?.criado_por?.nome || '') !== filterCriador) return false;
      if (dataInicio || dataFim) {
        const dt = new Date(item?.criado_em || item?.data_checklist || 0);
        if (dataInicio && dt < new Date(dataInicio)) return false;
        if (dataFim   && dt > new Date(dataFim + 'T23:59:59')) return false;
      }
      return true;
    });
  }, [filterLoja, filterCriador, dataInicio, dataFim, items]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link underline="hover" color="inherit" onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          <Home sx={{ mr: 0.5 }} fontSize="inherit" /> Dashboard
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/testes-loja-menu')}
          sx={{ cursor: 'pointer' }}
        >
          Testes de Loja
        </Link>
        <Typography color="text.primary">Consulta de checklists</Typography>
      </Breadcrumbs>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Consulta de checklists
              </Typography>
              <Typography color="text.secondary">
                Visualize os checklists TESTES (check list) já criados.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="outlined" onClick={() => navigate('/testes-checklist/novo')}>
                Novo checklist
              </Button>
              <Button variant="contained" onClick={() => carregar(dataInicio, dataFim)}>
                Atualizar
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Loja</InputLabel>
              <Select value={filterLoja} label="Loja" onChange={(e) => setFilterLoja(e.target.value)}>
                <MenuItem value="">Todas as lojas</MenuItem>
                {lojas.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Criador</InputLabel>
              <Select value={filterCriador} label="Criador" onChange={(e) => setFilterCriador(e.target.value)}>
                <MenuItem value="">Todos os criadores</MenuItem>
                {criadores.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small">
                <InputLabel shrink>Data início</InputLabel>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => { setDataInicio(e.target.value); carregar(e.target.value, dataFim); }}
                  style={{
                    marginTop: 16,
                    padding: '6px 10px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    fontSize: '0.875rem',
                    height: 36,
                  }}
                />
              </FormControl>
              <FormControl size="small">
                <InputLabel shrink>Data fim</InputLabel>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => { setDataFim(e.target.value); carregar(dataInicio, e.target.value); }}
                  style={{
                    marginTop: 16,
                    padding: '6px 10px',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    fontSize: '0.875rem',
                    height: 36,
                  }}
                />
              </FormControl>
            </Stack>

            {(filterLoja || filterCriador || dataInicio || dataFim) && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setFilterLoja(''); setFilterCriador(''); setDataInicio(primeiroDiaMes); setDataFim(hojeStr); carregar(primeiroDiaMes, hojeStr); }}
                sx={{ alignSelf: 'flex-end', height: 36 }}
              >
                Limpar filtros
              </Button>
            )}
          </Stack>

          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography>Carregando checklists...</Typography>
            </Stack>
          ) : null}

          {!loading && filtrados.length === 0 ? (
            <Alert severity="info">Nenhum checklist encontrado.</Alert>
          ) : null}

          <Grid container spacing={2}>
            {filtrados.map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item.checklist_id || item.arquivo_json_id}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Stack spacing={1.2}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {item.loja || 'Loja não informada'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Data checklist: {formatDateTime(item.data_checklist)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Criado em: {formatDateTime(item.criado_em)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Criado por: {item?.criado_por?.nome || 'Usuário'}
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getChecklistResumo(item)}
                    </Typography>

                    <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                      {Object.entries(item.checks || {}).map(([key, value]) => (
                        <Chip
                          key={`${item.checklist_id}-${key}`}
                          size="small"
                          color={value ? 'success' : 'default'}
                          label={`${CHECK_LABELS[key] || key}: ${value ? 'OK' : 'Não marcado'}`}
                        />
                      ))}
                    </Stack>

                    <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.observacoes || 'Sem observações'}
                    </Typography>

                    <Stack direction="row" spacing={1}>
                      <Button variant="text" onClick={() => handleVerDetalhes(item)} disabled={detailLoading}>
                        {detailLoading ? 'Carregando...' : 'Ver detalhes'}
                      </Button>
                      {isAdmin() && (
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          startIcon={<Delete fontSize="small" />}
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(item); }}
                        >
                          Excluir
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>Detalhes do checklist</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Stack spacing={2}>
              <Typography><strong>Loja:</strong> {selected.loja || '-'}</Typography>
              <Typography><strong>Data do checklist:</strong> {formatDateTime(selected.data_checklist)}</Typography>
              <Typography><strong>Criado em:</strong> {formatDateTime(selected.criado_em)}</Typography>
              <Typography><strong>Criado por:</strong> {selected?.criado_por?.nome || '-'}</Typography>

              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Checklist das fotos</Typography>
                <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
                  {Object.entries(selected.checks || {}).map(([key, value]) => (
                    <Chip
                      key={`selected-${key}`}
                      size="small"
                      color={value ? 'success' : 'default'}
                      label={`${CHECK_LABELS[key] || key}: ${value ? 'OK' : 'Não marcado'}`}
                    />
                  ))}
                </Stack>
              </Box>

              {selected.checklist_detalhado ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Refrigeração</Typography>
                    {renderSequencialDetalhe(selected.checklist_detalhado.refrigeracao || {})}
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Ar Condicionado</Typography>
                    {renderSequencialDetalhe(selected.checklist_detalhado.ar_condicionado || {})}
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Gerador</Typography>
                    <List dense>
                      {Object.entries(selected.checklist_detalhado.gerador || {}).map(([key, value]) => (
                        <ListItem key={`ger-${key}`}>
                          <ListItemText primary={`${key}: ${formatValueLabel(value)}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Porta Rápida - Câmara Resfriada</Typography>
                    <List dense>
                      {Object.entries(selected.checklist_detalhado?.portas_rapidas?.resfriada || {}).map(([key, value]) => (
                        <ListItem key={`pr-${key}`}>
                          <ListItemText primary={`${PORTA_LABELS[key] || key}: ${formatValueLabel(value)}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Porta Rápida - Câmara Congelada</Typography>
                    <List dense>
                      {Object.entries(selected.checklist_detalhado?.portas_rapidas?.congelada || {}).map(([key, value]) => (
                        <ListItem key={`pc-${key}`}>
                          <ListItemText primary={`${PORTA_LABELS[key] || key}: ${formatValueLabel(value)}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Box>
                    <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Observações por seção</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                      <Typography whiteSpace="pre-wrap">
                        Refrigeração: {selected.checklist_detalhado?.observacoes?.refrigeracao || '-'}
                      </Typography>
                      <Typography whiteSpace="pre-wrap">
                        Ar Condicionado: {selected.checklist_detalhado?.observacoes?.ar_condicionado || '-'}
                      </Typography>
                      <Typography whiteSpace="pre-wrap">
                        Gerador: {selected.checklist_detalhado?.observacoes?.gerador || '-'}
                      </Typography>
                      <Typography whiteSpace="pre-wrap">
                        Porta Resfriada: {selected.checklist_detalhado?.observacoes?.portas_resfriada || '-'}
                      </Typography>
                      <Typography whiteSpace="pre-wrap">
                        Porta Congelada: {selected.checklist_detalhado?.observacoes?.portas_congelada || '-'}
                      </Typography>
                    </Paper>
                  </Box>
                </Stack>
              ) : null}

              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Observações</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                  <Typography whiteSpace="pre-wrap">{selected.observacoes || 'Sem observações'}</Typography>
                </Paper>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.8 }}>Fotos salvas por aba</Typography>
                <Tabs
                  value={fotosTab}
                  onChange={(_, value) => setFotosTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 1.5 }}
                >
                  <Tab label={`Refrigeração (${groupedFotos.refrigeracao.length})`} />
                  <Tab label={`Ar Condicionado (${groupedFotos.ar.length})`} />
                  <Tab label={`Gerador (${groupedFotos.gerador.length})`} />
                  <Tab label={`Portas Rápidas (${groupedFotos.portas.length})`} />
                  <Tab label={`Finalização (${groupedFotos.finalizacao.length})`} />
                </Tabs>

                {fotosTab === 0 ? (
                  groupedFotos.refrigeracao.length ? (
                    <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                      {groupedFotos.refrigeracao.map((foto, index) => (
                        <UploadThumb
                          key={`f-ref-${foto.nome}-${foto._idx}-${index}`}
                          foto={foto}
                          subtitle={`Item: ${foto._itemLabel}`}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhuma foto em Refrigeração.</Alert>
                  )
                ) : null}

                {fotosTab === 1 ? (
                  groupedFotos.ar.length ? (
                    <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                      {groupedFotos.ar.map((foto, index) => (
                        <UploadThumb
                          key={`f-ar-${foto.nome}-${foto._idx}-${index}`}
                          foto={foto}
                          subtitle={`Item: ${foto._itemLabel}`}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhuma foto em Ar Condicionado.</Alert>
                  )
                ) : null}

                {fotosTab === 2 ? (
                  groupedFotos.gerador.length ? (
                    <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                      {groupedFotos.gerador.map((foto, index) => (
                        <UploadThumb
                          key={`f-ger-${foto.nome}-${foto._idx}-${index}`}
                          foto={foto}
                          subtitle={`Item: ${foto._itemLabel}`}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhuma foto em Gerador.</Alert>
                  )
                ) : null}

                {fotosTab === 3 ? (
                  groupedFotos.portas.length ? (
                    <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                      {groupedFotos.portas.map((foto, index) => (
                        <UploadThumb
                          key={`f-por-${foto.nome}-${foto._idx}-${index}`}
                          foto={foto}
                          subtitle={`Item: ${foto._itemLabel}`}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhuma foto em Portas Rápidas.</Alert>
                  )
                ) : null}

                {fotosTab === 4 ? (
                  groupedFotos.finalizacao.length ? (
                    <Stack direction="row" spacing={1.2} useFlexGap flexWrap="wrap">
                      {groupedFotos.finalizacao.map((foto, index) => (
                        <UploadThumb
                          key={`f-fin-${foto.nome}-${index}`}
                          foto={foto}
                          subtitle={foto.tamanho ? `${Math.round(foto.tamanho / 1024)} KB` : ''}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Nenhuma foto em Finalização.</Alert>
                  )
                ) : null}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Excluir checklist</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o checklist de{' '}
            <strong>{deleteTarget?.loja || 'esta loja'}</strong> criado em{' '}
            <strong>{formatDateTime(deleteTarget?.criado_em)}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(viewingFoto)}
        onClose={() => {
          setViewingFoto(null);
          setViewingFotoSrc('');
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: { backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' },
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
          {viewingFoto?.upload_id ? (
            <Box
              component="img"
              src={viewingFotoSrc}
              alt="Foto ampliada"
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
              onError={() => {
                console.error('Erro ao carregar imagem');
              }}
            />
          ) : null}
          <Button
            onClick={() => {
              setViewingFoto(null);
              setViewingFotoSrc('');
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.6)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
              minWidth: 'auto',
              padding: '8px 12px',
            }}
          >
            ✕
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
